import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

export type Theme = "light" | "dark";

const THEME_KEY = "mo-theme";

function systemTheme(): Theme {
	if (typeof window === "undefined") return "light";
	return window.matchMedia("(prefers-color-scheme: dark)").matches
		? "dark"
		: "light";
}

function readStoredTheme(): Theme | null {
	try {
		const raw = localStorage.getItem(THEME_KEY);
		if (raw === "light" || raw === "dark") return raw;
	} catch {
		/* ignore */
	}
	return null;
}

function applyTheme(theme: Theme) {
	document.documentElement.dataset.theme = theme;
	document.documentElement.style.colorScheme = theme;
	const meta = document.querySelector('meta[name="theme-color"]');
	if (meta) {
		meta.setAttribute("content", theme === "dark" ? "#141311" : "#f6f5f0");
	}
}

type UiContextValue = {
	searchOpen: boolean;
	privacyOpen: boolean;
	notice: string;
	theme: Theme;
	toggleTheme: () => void;
	setThemePreference: (theme: Theme) => void;
	openSearch: () => void;
	closeSearch: () => void;
	openPrivacy: () => void;
	closePrivacy: () => void;
	showNotice: (message: string) => void;
};

const UiContext = createContext<UiContextValue | null>(null);

export function UiProvider({ children }: { children: ReactNode }) {
	const [searchOpen, setSearchOpen] = useState(false);
	const [privacyOpen, setPrivacyOpen] = useState(false);
	const [notice, setNotice] = useState("");
	const [theme, setTheme] = useState<Theme>(() => {
		if (typeof window === "undefined") return "light";
		return readStoredTheme() ?? systemTheme();
	});
	const [themeLocked, setThemeLocked] = useState(
		() => typeof window !== "undefined" && readStoredTheme() !== null,
	);

	const closeSearch = useCallback(() => setSearchOpen(false), []);
	const closePrivacy = useCallback(() => setPrivacyOpen(false), []);

	useEffect(() => {
		applyTheme(theme);
	}, [theme]);

	useEffect(() => {
		if (themeLocked) return;
		const media = window.matchMedia("(prefers-color-scheme: dark)");
		const sync = () => setTheme(media.matches ? "dark" : "light");
		sync();
		media.addEventListener("change", sync);
		return () => media.removeEventListener("change", sync);
	}, [themeLocked]);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
				event.preventDefault();
				setSearchOpen((open) => !open);
			}
			if (event.key === "Escape") {
				setSearchOpen(false);
				setPrivacyOpen(false);
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	useEffect(() => {
		document.body.classList.toggle("modal-open", searchOpen || privacyOpen);
	}, [searchOpen, privacyOpen]);

	const setThemePreference = useCallback((next: Theme) => {
		try {
			localStorage.setItem(THEME_KEY, next);
		} catch {
			/* ignore */
		}
		setThemeLocked(true);
		setTheme(next);
	}, []);

	const toggleTheme = useCallback(() => {
		setThemePreference(theme === "dark" ? "light" : "dark");
	}, [setThemePreference, theme]);

	const value = useMemo(
		() => ({
			searchOpen,
			privacyOpen,
			notice,
			theme,
			toggleTheme,
			setThemePreference,
			openSearch: () => setSearchOpen(true),
			closeSearch,
			openPrivacy: () => setPrivacyOpen(true),
			closePrivacy,
			showNotice: (message: string) => {
				setNotice(message);
				window.setTimeout(() => setNotice(""), 1400);
			},
		}),
		[
			searchOpen,
			privacyOpen,
			notice,
			theme,
			toggleTheme,
			setThemePreference,
			closeSearch,
			closePrivacy,
		],
	);

	return <UiContext.Provider value={value}>{children}</UiContext.Provider>;
}

export function useUi() {
	const ctx = useContext(UiContext);
	if (!ctx) {
		throw new Error("useUi must be used inside UiProvider");
	}
	return ctx;
}
